import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  deleteFamilyMember,
  listFamilyMembers,
  updateFamilyMember,
} from "../lib/api-modules/family-members.js";
import type {
  FamilyMember,
  FamilyRelationship,
  MembershipRole,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { NotesPanel } from "../components/notes-panel.js";
import { FamilyMemberForm } from "../components/family/FamilyMemberForm.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /family — household-scoped CRUD for the people in the household.
 *
 * Read access for every member; write access for owner/admin/member/
 * contributor. Soft-delete (archive) preserves account beneficiary FKs;
 * the SPA renders an `(archived)` badge on archived rows.
 *
 * Notes panel embedded per-member for capturing things like "Mom turning
 * 75 — Medicare planning conversation" or daughter's milestones.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

const RELATIONSHIP_LABELS: Readonly<Record<FamilyRelationship, string>> = {
  self: "Self",
  spouse: "Spouse",
  parent: "Parent",
  child: "Child",
  sibling: "Sibling",
  grandparent: "Grandparent",
  grandchild: "Grandchild",
  other: "Other",
};

export function FamilyPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Family</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            People in your household — used for beneficiary linkage on
            529/UTMA accounts and college planning.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <FamilyMembersList canWrite={canWrite} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.family} />
      </div>
    </div>
  );
}

function FamilyMembersList({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["family-members", { includeArchived: showArchived }],
    queryFn: () => listFamilyMembers(showArchived),
  });

  const archiveMutation = useMutation({
    mutationFn: (memberId: string) => deleteFamilyMember(memberId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["family-members"] });
      toast.show("Member archived");
    },
    onError: (err) => {
      toast.show(
        err instanceof ApiError
          ? `Couldn't archive: ${err.message}`
          : "Couldn't archive member",
      );
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (memberId: string) =>
      updateFamilyMember(memberId, { is_archived: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["family-members"] });
      toast.show("Member restored");
    },
    onError: () => toast.show("Couldn't restore member"),
  });

  if (isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading family…
      </p>
    );
  }
  if (error) {
    return (
      <ErrorMessage>
        {error instanceof ApiError ? error.message : "Couldn't load family."}
      </ErrorMessage>
    );
  }

  const members = data?.family_members ?? [];

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-zinc-600 dark:text-zinc-400">
          {members.length} {members.length === 1 ? "member" : "members"}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {canWrite && !showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
            >
              Add member
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="mb-4">
          <FamilyMemberForm
            mode="create"
            onClose={() => setShowCreate(false)}
          />
        </div>
      )}

      {members.length === 0 && !showCreate && (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No family members yet.{" "}
          {canWrite ? "Click \"Add member\" to start." : ""}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {members.map((m) => (
          <li
            key={m.member_id}
            className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
          >
            {editingId === m.member_id ? (
              <FamilyMemberForm
                mode="edit"
                member={m}
                onClose={() => setEditingId(null)}
              />
            ) : (
              <FamilyMemberCard
                member={m}
                canWrite={canWrite}
                onEdit={() => setEditingId(m.member_id)}
                onArchive={() => archiveMutation.mutate(m.member_id)}
                onRestore={() => restoreMutation.mutate(m.member_id)}
                isMutating={
                  archiveMutation.isPending || restoreMutation.isPending
                }
              />
            )}
          </li>
        ))}
      </ul>

      <Toast message={toast.message} />
    </section>
  );
}

function FamilyMemberCard({
  member,
  canWrite,
  onEdit,
  onArchive,
  onRestore,
  isMutating,
}: {
  member: FamilyMember;
  canWrite: boolean;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  isMutating: boolean;
}) {
  const [showNotes, setShowNotes] = useState(false);
  const ageText = member.date_of_birth
    ? formatAge(member.date_of_birth)
    : null;

  return (
    <article>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-medium">
            {member.name}
            {member.is_archived && (
              <span className="ml-2 rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                archived
              </span>
            )}
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            {RELATIONSHIP_LABELS[member.relationship]}
            {ageText && <> · {ageText}</>}
            {member.email && <> · {member.email}</>}
          </p>
          {member.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {member.notes}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-1.5">
            {!member.is_archived ? (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  disabled={isMutating}
                  className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
                >
                  Edit
                </button>
                <ConfirmButton
                  label="Archive"
                  pendingLabel="Archiving…"
                  size="xs"
                  ariaLabel={`Archive ${member.name}`}
                  onConfirm={onArchive}
                  isPending={isMutating}
                />
              </>
            ) : (
              <button
                type="button"
                onClick={onRestore}
                disabled={isMutating}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Restore
              </button>
            )}
          </div>
        )}
      </header>

      {!member.is_archived && member.relationship === "child" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={`/family/${member.member_id}/allowance`}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            Allowance →
          </Link>
          <Link
            to={`/family/${member.member_id}/buckets`}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            Buckets →
          </Link>
        </div>
      )}

      <div className="mt-3 border-t border-zinc-200 pt-3 dark:border-zinc-800">
        <button
          type="button"
          onClick={() => setShowNotes((s) => !s)}
          className="text-xs text-zinc-500 hover:underline"
        >
          {showNotes ? "Hide notes" : "Show notes"}
        </button>
        {showNotes && (
          <div className="mt-3">
            <NotesPanel
              entityType="family_member"
              entityId={member.member_id}
            />
          </div>
        )}
      </div>
    </article>
  );
}

function formatAge(dob: string): string {
  try {
    const years = Math.max(0, computeYears(dob));
    if (years === 0) return "infant";
    if (years === 1) return "1 yr old";
    return `${years} yrs old${years < 18 ? " (minor)" : ""}`;
  } catch {
    return "";
  }
}

/** Local helper — same algorithm as `shared/util/family.ageInYears`. */
function computeYears(dob: string): number {
  const parts = dob.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const ref = new Date();
  let age = ref.getUTCFullYear() - y;
  const refMonth = ref.getUTCMonth() + 1;
  const refDay = ref.getUTCDate();
  if (refMonth < m || (refMonth === m && refDay < d)) age -= 1;
  return age;
}
